import React, { useEffect, useState } from "react";
import cx from "classnames";
import styles from "../scss/AFQuestionForm.module.scss";
import { Form, Formik } from "formik";
import AFMultiFormField from "./AFMultiFormField";
import { IAnswerProps, IAppFormQuestion } from "../interfaces/IAppFormData";
import { checkInValidAnswer, getQuestionHeaderText } from "../shared/AppFormHelper";
import { QuestionType } from "../enums/QuestionType";
import AFCheckboxListField from "./AFCheckboxListField";
import AFRadioListField from "./AFRadioListField";
import useAFForm from "../hooks/useAFForm";
import AFNavigation from "./AFNavigation";

interface IAFMultiFormsProps {
    applicantName: string;
    list: IAppFormQuestion[];
    nextQuestion: (any) => void;
    formLabel?: string;
    helpText?: string;
    formQuestionType?: string;
    handleBack: (e) => void;
    skipIsVisible: boolean;
    handleSkip?: () => void;
    showSkipDialog?: boolean;
}

const AFMultiForms: React.FC<IAFMultiFormsProps> = (props) => {
    const { list, applicantName, formQuestionType, handleBack, skipIsVisible, handleSkip, showSkipDialog } = props;
    const [isReady, setIsReady] = useState(false);
    const [questionReady, setQuestionReady] = useState(false);
    const [formQuestionHeaderText, setFormQuestionHeaderText] = useState("");
    const [formAnswerList, setFormAnswerList] = useState<IAnswerProps[]>([]);
    const [formReadyToSubmit, setFormReadyToSubmit] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState<any>(null);

    const { checkFormCheckList } = useAFForm();

    const checkForms = (params): void => {
        if (formQuestionType === QuestionType.CHECKLIST) {
            setFormReadyToSubmit(checkFormCheckList(params));
            return;
        }
        const isReady = params.reduce((acc, item) => {
            if (checkInValidAnswer(item?.answer)) {
                acc = false;
            }
            return acc;
        }, true);
        setFormReadyToSubmit(isReady);
    };

    const handleAnswerChanges = (answerList: IAnswerProps[]): void => {
        checkForms(answerList);
        setFormAnswerList(answerList);
        if (formQuestionType === QuestionType.RADIOLIST && !checkRadioListValue()) {
            const timeout = setTimeout(() => {
                props.nextQuestion(answerList);
            }, 500);

            setSearchTimeout(timeout);
        }
    };

    const checkRadioListValue = (): boolean => {
        return !!list.find((i) => i?.answer)?.id || false;
    };

    const handleNextQuestion = (): void => {
        if (!formReadyToSubmit) return;
        props.nextQuestion(formAnswerList);
    };

    const handleButtonVisibility = (questionType: string): boolean => {
        switch (questionType) {
            case QuestionType.RADIOLIST:
                const buttonStatus = checkRadioListValue() || false;
                return buttonStatus;
            default:
                return true;
        }
    };

    useEffect(() => {
        if (list?.length) {
            setIsReady(true);
            setFormQuestionHeaderText(getQuestionHeaderText(list[0]));
            const answerList = list.reduce((acc, item) => {
                return [
                    ...acc,
                    {
                        questionId: item.id,
                        disclosure: [],
                        answer: ""
                    }
                ];
            }, []);
            setFormAnswerList(answerList);
        }
        return () => {
            clearTimeout(searchTimeout);
        };
    }, []);

    if (formQuestionType === QuestionType.QUESTION_LIST) {
        return (
            <AFMultiFormField
                applicantName={applicantName}
                list={list}
                formQuestionLabel={props.formLabel}
                nextQuestion={props.nextQuestion}
                fieldReady={setQuestionReady}
                handleBack={handleBack}
                skipIsVisible={skipIsVisible}
                handleSkip={handleSkip}
                showSkipDialog={showSkipDialog}
            />
        );
    } else {
        return (
            <div
                className={cx(
                    styles["fa-question"]
                )}>
                <div className={styles["form-title"]}>
                    <div
                        className={styles["form-title-client"]}
                        dangerouslySetInnerHTML={{
                            __html: applicantName
                        }}></div>
                </div>
                {props.formLabel && (
                    <div className={styles["form-title"]}>
                        <label
                            className={styles["form-title-label"]}
                            htmlFor={props.formLabel}
                            dangerouslySetInnerHTML={{
                                __html: props.formLabel || ""
                            }}></label>
                    </div>
                )}

                {isReady && (
                    <Formik initialValues={{}} onSubmit={handleNextQuestion}>
                        {() => (
                            <Form autoComplete="off" className={styles["fa-question-form"]}>
                                <div
                                    className={cx(
                                        styles["question-form"],
                                        "layout-flex-column--centered"
                                    )}>
                                    {formQuestionHeaderText &&
                                        props.formLabel !== formQuestionHeaderText && (
                                            <label
                                                className={styles["question-form-label"]}
                                                dangerouslySetInnerHTML={{
                                                    __html: formQuestionHeaderText || ""
                                                }}></label>
                                        )}

                                    {formQuestionType === QuestionType.CHECKLIST && (
                                        <AFCheckboxListField
                                            list={list}
                                            formAnswerList={formAnswerList}
                                            answerChanges={handleAnswerChanges}
                                            label={props.formLabel}
                                            helpText={props.helpText}
                                        />
                                    )}
                                    {formQuestionType === QuestionType.RADIOLIST && (
                                        <AFRadioListField
                                            list={list}
                                            formAnswerList={formAnswerList}
                                            answerChanges={handleAnswerChanges}
                                            label={props.formLabel}
                                            helpText={props.helpText}
                                        />
                                    )}
                                </div>
                                <AFNavigation
                                    handleBack={handleBack}
                                    skipIsVisible={skipIsVisible}
                                    showSkipDialog={showSkipDialog}
                                    handleSkip={handleSkip}
                                    forwardIsVisible={handleButtonVisibility(formQuestionType)}
                                    forwardIsDisabled={!formReadyToSubmit}
                                    forwardType="submit"
                                    forwardText={"Next"}
                                /> 
                            </Form>
                        )}
                    </Formik>
                )}
            </div>
        );
    }
};

export default AFMultiForms;
